import { AuditDetailItem, MovedToBackstockItem } from './AuditDetailModel';
import type { AuditSummaryTotals } from '../../features/audits/types';

export interface DailyReportAuditItem {
    audit_id: string;
    audit_date: string;
    user_name: string;
    audit_summary: {
        total_sold: number | string;
        total_revenue: number;
        total_packs: number;
        total_games: number;
        newly_activated_count: number;
        newly_activated_value: number;
        opening_count: number | string;
        closing_count: number | string;
        opening_value: number;
        closing_value: number;
        moved_to_backstock_count?: number;
    };
    pos_summary: {
        scratcher_count: number;
        scratcher_sales: number;
        lotto_sales: number;
        payout: number;
        scratcher_free_tickets_paid_amount?: number;
        scratcher_free_tickets_paid_count?: number;
    };
    lotto_summary: {
        sales: number;
        payouts_total: number;
        payouts_count?: number;
        lotto_payouts_amount?: number;
        lotto_payouts_count?: number;
        scratcher_payouts_amount?: number;
        scratcher_payouts_count?: number;
        activated_count?: number;
        activated_pack_count?: number;
        return_count?: number;
        return_value_total?: number;
        scratcher_free_tickets_paid_count?: number;
        scratcher_free_tickets_paid_amount?: number;
    };
    reconciliation_variance: {
        scratcher: {
            quantity_variance: number;
            revenue_variance: number;
        };
        lotto_sales_variance: number;
        payout_variance: number;
        total_variance: number;
    };
}

/**
 * Daily Report Model - Encapsulates daily expanded report data
 * Follows AuditModel.ts pattern with individual getters
 */
export class DailyReportModel {
    private _serverData: any;
    private _items: AuditDetailItem[] | null = null;
    private _movedToBackstockItems: MovedToBackstockItem[] | null = null;

    constructor(data: any) {
        this._serverData = data;
    }

    private _parseNum(val: any): number {
        if (typeof val === 'string') return parseFloat(val) || 0;
        return val ?? 0;
    }

    // Individual Getters - Card 1 (Lotto)
    get lottoSales(): number {
        return this._parseNum(this._serverData?.daily_totals?.lotto?.sales);
    }

    get lottoSalesVariance(): number {
        return this._parseNum(this._serverData?.daily_totals?.reconciliation_variance?.lotto_sales_variance);
    }

    get payouts(): number {
        return this._parseNum(this._serverData?.daily_totals?.lotto?.payouts_total);
    }

    get payoutsVariance(): number {
        return this._parseNum(this._serverData?.daily_totals?.reconciliation_variance?.payout_variance);
    }

    get totalVariance(): number {
        return this._parseNum(this._serverData?.daily_totals?.reconciliation_variance?.total_variance);
    }

    // Individual Getters - Card 2 (Scratchers)
    get scratcherCount(): number {
        return this._parseNum(this._serverData?.daily_totals?.pos?.scratcher_count);
    }

    get scratcherCountVariance(): number {
        return this._parseNum(this._serverData?.daily_totals?.reconciliation_variance?.scratcher?.quantity_variance);
    }

    get scratcherSales(): number {
        return this._parseNum(this._serverData?.daily_totals?.pos?.scratcher_sales);
    }

    get scratcherSalesVariance(): number {
        return this._parseNum(this._serverData?.daily_totals?.reconciliation_variance?.scratcher?.revenue_variance);
    }

    get posLottoSales(): number {
        return this._parseNum(this._serverData?.daily_totals?.pos?.lotto_sales);
    }

    get posPayout(): number {
        return this._parseNum(this._serverData?.daily_totals?.pos?.payout);
    }

    get posFreeTicketsAmount(): number {
        return this._parseNum(this._serverData?.daily_totals?.pos?.scratcher_free_tickets_paid_amount);
    }

    get posFreeTicketsCount(): number {
        return this._parseNum(this._serverData?.daily_totals?.pos?.scratcher_free_tickets_paid_count);
    }

    get posDataReceivedAtFrom(): string {
        return String(this._serverData?.pos_data_received_at?.from ?? '');
    }

    get posDataReceivedAtTo(): string {
        return String(this._serverData?.pos_data_received_at?.to ?? '');
    }

    get stateLotteryDataReceivedAt(): string {
        return String(this._serverData?.state_lottery_data_received_at ?? '');
    }

    // Individual Getters - Bottom Row (Small Cards)
    get freeTicketsAmount(): number {
        return this._parseNum(this._serverData?.daily_totals?.lotto?.scratcher_free_tickets_paid_amount);
    }

    get freeTicketsCount(): number {
        return this._parseNum(this._serverData?.daily_totals?.lotto?.scratcher_free_tickets_paid_count);
    }

    get scratcherPayoutsAmount(): number {
        return this._parseNum(this._serverData?.daily_totals?.lotto?.scratcher_payouts_amount);
    }

    get lottoPayoutsTotal(): number {
        return this._parseNum(
            this._serverData?.daily_totals?.lotto?.lotto_payouts_amount
        );
    }

    // Individual Getters - Audit Object
    get auditTotalSold(): string | number {
        return this._serverData?.daily_totals?.audit?.total_sold ?? 0;
    }

    get auditTotalRevenue(): number {
        return this._parseNum(this._serverData?.daily_totals?.audit?.total_revenue);
    }

    get auditTotalPacks(): number {
        return this._parseNum(this._serverData?.daily_totals?.audit?.total_packs);
    }

    get auditTotalGames(): number {
        return this._parseNum(this._serverData?.daily_totals?.audit?.total_games);
    }

    get auditNewlyActivatedCount(): number {
        return this._parseNum(this._serverData?.daily_totals?.audit?.newly_activated_count);
    }

    get auditNewlyActivatedValue(): number {
        return this._parseNum(this._serverData?.daily_totals?.audit?.newly_activated_value);
    }

    get auditOpeningCount(): string | number {
        return this._serverData?.daily_totals?.audit?.opening_count ?? 0;
    }

    get auditClosingCount(): string | number {
        return this._serverData?.daily_totals?.audit?.closing_count ?? 0;
    }

    get auditOpeningValue(): number {
        return this._parseNum(this._serverData?.daily_totals?.audit?.opening_value);
    }

    get auditClosingValue(): number {
        return this._parseNum(this._serverData?.daily_totals?.audit?.closing_value);
    }

    /**
     * Shift-level audits array from daily expanded report
     */
    get audits(): DailyReportAuditItem[] {
        const sourceAudits = this._serverData?.audits;
        if (!Array.isArray(sourceAudits)) return [];

        return sourceAudits.map((audit: any) => ({
            audit_id: String(audit?.audit_id ?? ''),
            audit_date: String(audit?.audit_date ?? ''),
            user_name: String(audit?.user_name ?? ''),
            audit_summary: {
                total_sold: audit?.audit_summary?.total_sold ?? 0,
                total_revenue: this._parseNum(audit?.audit_summary?.total_revenue),
                total_packs: this._parseNum(audit?.audit_summary?.total_packs),
                total_games: this._parseNum(audit?.audit_summary?.total_games),
                newly_activated_count: this._parseNum(audit?.audit_summary?.newly_activated_count),
                newly_activated_value: this._parseNum(audit?.audit_summary?.newly_activated_value),
                opening_count: audit?.audit_summary?.opening_count ?? 0,
                closing_count: audit?.audit_summary?.closing_count ?? 0,
                opening_value: this._parseNum(audit?.audit_summary?.opening_value),
                closing_value: this._parseNum(audit?.audit_summary?.closing_value),
                moved_to_backstock_count: this._parseNum(audit?.audit_summary?.moved_to_backstock_count),
            },
            pos_summary: {
                scratcher_count: this._parseNum(audit?.pos_summary?.scratcher_count),
                scratcher_sales: this._parseNum(audit?.pos_summary?.scratcher_sales),
                lotto_sales: this._parseNum(audit?.pos_summary?.lotto_sales),
                payout: this._parseNum(audit?.pos_summary?.payout),
                scratcher_free_tickets_paid_amount: this._parseNum(audit?.pos_summary?.scratcher_free_tickets_paid_amount),
            },
            lotto_summary: {
                sales: this._parseNum(audit?.lotto_summary?.sales),
                payouts_total: this._parseNum(audit?.lotto_summary?.payouts_total),
                payouts_count: this._parseNum(audit?.lotto_summary?.payouts_count),
                lotto_payouts_amount: this._parseNum(audit?.lotto_summary?.lotto_payouts_amount),
                lotto_payouts_count: this._parseNum(audit?.lotto_summary?.lotto_payouts_count),
                scratcher_payouts_amount: this._parseNum(audit?.lotto_summary?.scratcher_payouts_amount),
                scratcher_payouts_count: this._parseNum(audit?.lotto_summary?.scratcher_payouts_count),
                activated_count: this._parseNum(audit?.lotto_summary?.activated_count),
                activated_pack_count: this._parseNum(audit?.lotto_summary?.activated_pack_count),
                return_count: this._parseNum(audit?.lotto_summary?.return_count),
                return_value_total: this._parseNum(audit?.lotto_summary?.return_value_total),
                scratcher_free_tickets_paid_count: this._parseNum(audit?.lotto_summary?.scratcher_free_tickets_paid_count),
                scratcher_free_tickets_paid_amount: this._parseNum(audit?.lotto_summary?.scratcher_free_tickets_paid_amount),
            },
            reconciliation_variance: {
                scratcher: {
                    quantity_variance: this._parseNum(audit?.reconciliation_variance?.scratcher?.quantity_variance),
                    revenue_variance: this._parseNum(audit?.reconciliation_variance?.scratcher?.revenue_variance),
                },
                lotto_sales_variance: this._parseNum(audit?.reconciliation_variance?.lotto_sales_variance),
                payout_variance: this._parseNum(audit?.reconciliation_variance?.payout_variance),
                total_variance: this._parseNum(audit?.reconciliation_variance?.total_variance),
            },
        }));
    }

    /**
     * Lazy-loaded moved-to-backstock items for the dedicated tab
     */
    get movedToBackstockItems(): MovedToBackstockItem[] {
        if (this._movedToBackstockItems) return this._movedToBackstockItems;

        const sourceItems = this._serverData?.moved_to_backstock;
        if (!Array.isArray(sourceItems)) {
            this._movedToBackstockItems = [];
            return this._movedToBackstockItems;
        }

        this._movedToBackstockItems = sourceItems.map((item: any, idx: number) => {
            const ticketNumberAtMove = this._parseNum(item?.ticket_number_at_move);
            const ticketsRemaining = this._parseNum(item?.tickets_remaining);
            const start = Number(ticketNumberAtMove % 1000);
            const endRaw = ticketsRemaining === 0 ? ticketNumberAtMove : (ticketNumberAtMove + ticketsRemaining);
            const end = Number(endRaw % 1000);
            const range = `${start.toString().padStart(3, '0')}-${end.toString().padStart(3, '0')}`;
            const costPerTicket = this._parseNum(item?.game_cost_per_ticket);

            return {
                id: `${item?.from_audit_id ?? 'from'}-${item?.to_audit_id ?? 'to'}-${item?.game_id ?? 'game'}-${item?.pack_id ?? idx}`,
                gameId: String(item?.game_id ?? ''),
                gameName: String(item?.game_name ?? ''),
                plu: String(item?.game_PLU ?? ''),
                costPerTicket,
                packId: String(item?.pack_id ?? ''),
                ticketNumberAtMove,
                ticketsRemaining,
                movedAt: item?.moved_at,
                fromAuditId: item?.from_audit_id,
                toAuditId: item?.to_audit_id,
                moved_to_backstock_range: range,
                moved_to_backstock_count: ticketsRemaining,
                moved_to_backstock_value: ticketsRemaining * costPerTicket
            };
        });

        return this._movedToBackstockItems;
    }

    /**
     * Lazy-loaded items for the table
     */
    get items(): AuditDetailItem[] {
        if (this._items) return this._items;

        this._items = [];
        if (!this._serverData?.games) return this._items;

        this._serverData.games.forEach((game: any) => {
            const packs = game.packs || [];
            
            // If game has packs, create items for each pack
            if (packs.length > 0) {
                packs.forEach((pack: any) => {
                    this._items!.push(new AuditDetailItem(
                        `${game.game_id}-${pack.pack_id}`,
                        game.game_id,
                        game.game_name,
                        String(game.plu),
                        game.cost_per_ticket,
                        String(pack.pack_id),
                        pack.starting_ticket_number,
                        pack.ending_ticket_number,
                        pack.qty_sold,
                        game.pos_totals?.scratcher_count ?? 0,
                        game.pos_totals?.scratcher_sales ?? 0,
                        game.reconciliation_variance?.quantity_variance ?? 0,
                        game.reconciliation_variance?.revenue_variance ?? 0,
                        pack.is_newly_activated,
                        pack.starting_ticket_display,
                        pack.ending_ticket_display,
                        Boolean(pack.is_manual_sold_out),
                        pack.validation_message
                    ));
                });
            } else {
                // If game has no packs (packs.length === 0), create one item with empty pack data
                this._items!.push(new AuditDetailItem(
                    `${game.game_id}-no-pack`,
                    game.game_id,
                    game.game_name,
                    String(game.plu),
                    game.cost_per_ticket,
                    '', // Empty packId
                    0,  // Empty startingTicket
                    0,  // Empty endingTicket
                    0,  // Empty qtySold
                    game.pos_totals?.scratcher_count ?? 0,
                    game.pos_totals?.scratcher_sales ?? 0,
                    game.reconciliation_variance?.quantity_variance ?? 0,
                    game.reconciliation_variance?.revenue_variance ?? 0,
                    false, // isNewlyActivated
                    undefined,
                    undefined,
                    false,
                    game.validation_message
                ));
            }
        });

        return this._items;
    }

   

    /**
     * summaryTotals provides a structured object for the AuditSummary component
     */
    get summaryTotals(): AuditSummaryTotals {
        return {
            audit: {
                total_sold: this.auditTotalSold,
                total_revenue: this.auditTotalRevenue,
                total_packs: this.auditTotalPacks,
                total_games: this.auditTotalGames,
                newly_activated_count: this.auditNewlyActivatedCount,
                newly_activated_value: this.auditNewlyActivatedValue,
                opening_count: this.auditOpeningCount,
                closing_count: this.auditClosingCount,
                opening_value: this.auditOpeningValue,
                closing_value: this.auditClosingValue
            },
            pos: {
                scratcher_count: this.scratcherCount,
                scratcher_sales: this.scratcherSales,
                lotto_sales: this.posLottoSales,
                payout: this.posPayout,
                scratcher_free_tickets_paid_amount: this.posFreeTicketsAmount,
                scratcher_free_tickets_paid_count: this.posFreeTicketsCount,
            },
            reconciliation_variance: {
                scratcher: {
                    quantity_variance: this.scratcherCountVariance,
                    revenue_variance: this.scratcherSalesVariance
                },
                lotto_sales_variance: this.lottoSalesVariance,
                payout_variance: this.payoutsVariance,
                total_variance: this.totalVariance
            },
            lotto: {
                sales: this.lottoSales,
                payouts_total: this.payouts,
                scratcher_free_tickets_paid_count: this.freeTicketsCount,
                scratcher_free_tickets_paid_amount: this.freeTicketsAmount,
                scratcher_payouts_amount: this.scratcherPayoutsAmount,
                lotto_payouts_total: this.lottoPayoutsTotal
            }
        };
    }

    static fromServerData(data: any): DailyReportModel {
        return new DailyReportModel(data);
    }
}
